import { useRef, useState } from 'react';
import { focusGooglePopup, signInWithGoogle } from './googleSignIn.js';
import { isMicrosoftSuperseded, signInWithMicrosoft } from '../login/microsoftSignIn.js';
import { completeOAuthSignIn } from './completeOAuthSignIn.js';

/**
 * Social sign-in state shared by every form that renders SocialButtons.
 *
 * `phase`: 'idle' | 'google' (popup open, no token yet) | 'microsoft' (MSAL popup)
 * | 'exchanging' (token sent to the API). While 'google' the Google button stays
 * usable: a click focuses the open popup, or restarts once it has closed.
 *
 * While 'microsoft' nothing is locked: we cannot see Microsoft's popup (COOP), so
 * a click opens another popup in the same wait and the first token of that wait
 * wins. Google or a password submit abandons the wait; its late results are ignored.
 *
 * @param {{
 *   clientIds: {google: string|null, microsoft: string|null},
 *   redirectTo: string,
 *   onStart: () => void,
 *   onError: (err: unknown) => void
 * }} options `onError` gets SDK and API errors alike; the phase is already 'idle'.
 */
export function useSocialSignIn({ clientIds, redirectTo, onStart, onError }) {
	const [phase, setPhase] = useState('idle');
	// The ref sees same-tick updates, so a second token can never start a second exchange.
	const phaseRef = useRef('idle');
	const attemptRef = useRef(0);
	const waitRef = useRef(0);

	const moveTo = next => {
		phaseRef.current = next;
		setPhase(next);
	};

	const fail = err => {
		moveTo('idle');
		onError(err);
	};

	const exchange = async (provider, idToken) => {
		moveTo('exchanging');
		try {
			await completeOAuthSignIn(provider, idToken, { redirectTo });
		} catch (err) {
			fail(err);
		}
	};

	const cancelMicrosoftWait = () => {
		if (phaseRef.current !== 'microsoft') return;
		waitRef.current += 1;
		moveTo('idle');
	};

	const isExchanging = () => phaseRef.current === 'exchanging';

	const signInGoogle = () => {
		if (phaseRef.current === 'exchanging') return;
		if (phaseRef.current === 'google' && focusGooglePopup()) return;
		cancelMicrosoftWait();
		const attempt = ++attemptRef.current;
		onStart();
		moveTo('google');
		signInWithGoogle(clientIds.google).then(
			idToken => {
				if (attempt === attemptRef.current) exchange('google', idToken);
			},
			err => {
				if (attempt === attemptRef.current) fail(err);
			}
		);
	};

	const signInMicrosoft = () => {
		if (phaseRef.current === 'exchanging' || phaseRef.current === 'google') return;
		if (phaseRef.current === 'idle') {
			waitRef.current += 1;
			onStart();
			moveTo('microsoft');
		}
		const wait = waitRef.current;
		const attempt = ++attemptRef.current;
		const isLive = () => wait === waitRef.current && phaseRef.current === 'microsoft';
		signInWithMicrosoft(clientIds.microsoft).then(
			idToken => {
				if (isLive()) exchange('microsoft', idToken);
			},
			err => {
				// Only the newest attempt may end the wait; an older popup failing says nothing about it.
				if (isLive() && attempt === attemptRef.current && !isMicrosoftSuperseded(err)) fail(err);
			}
		);
	};

	return { phase, signInGoogle, signInMicrosoft, cancelMicrosoftWait, isExchanging };
}
